import { useRef, useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useSaveBulkRules } from './useSaveBulkRules';

const ERROR_MESSAGE = (
    <FormattedMessage
        id="codeless.save.and.close.error"
        defaultMessage="Sorry, an error occurred while saving the event. Please try again. If the problem persists, please contact support."
    />
);

export const useSaveAndClose = ({ touchedConversions }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState(ERROR_MESSAGE);
    const startTimeRef = useRef(null);
    const timeoutRef = useRef(null);
    const saveCompletedRef = useRef(false);
    const MIN_LOADING_TIME = 1000;
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
            clearTimeout(timeoutRef.current);
        };
    }, []);

    const showSuccessWithMinLoadingTime = useCallback(() => {
        saveCompletedRef.current = true;
        const elapsed = Date.now() - startTimeRef.current;

        const show = () => {
            if (!isMounted.current) return;
            setIsLoading(false);
            setShowSuccessModal(true);
        };

        const delay = Math.max(MIN_LOADING_TIME - elapsed, 0);
        setTimeout(show, delay);
    }, []);

    const showErrorWithMinLoadingTime = useCallback(() => {
        saveCompletedRef.current = true;
        const elapsed = Date.now() - startTimeRef.current;

        const show = () => {
            if (!isMounted.current) return;
            setIsLoading(false);
            setShowErrorModal(true);
        };

        setErrorMessage(ERROR_MESSAGE);
        const delay = Math.max(MIN_LOADING_TIME - elapsed, 0);
        setTimeout(show, delay);
    }, []);

    const saveBulkRules = useSaveBulkRules({
        onSuccess: showSuccessWithMinLoadingTime,
        onFailure: showErrorWithMinLoadingTime,
    });

    const handleSaveAndClose = useCallback(async () => {
        if (isLoading) return;

        setIsLoading(true);
        startTimeRef.current = Date.now();
        saveCompletedRef.current = false;

        if (touchedConversions.length === 0) {
            showSuccessWithMinLoadingTime();
            return;
        }

        timeoutRef.current = setTimeout(() => {
            if (!saveCompletedRef.current) {
                if (!isMounted.current) return;
                setIsLoading(false);
                setShowErrorModal(true);
                setErrorMessage(ERROR_MESSAGE);
            }
        }, 10000);

        try {
            const savePromise = saveBulkRules();
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => {
                    if (!saveCompletedRef.current) {
                        reject(new Error('Save operation timed out'));
                    }
                }, 12000)
            );

            await Promise.race([savePromise, timeoutPromise]);

            if (!saveCompletedRef.current) {
                showErrorWithMinLoadingTime();
            }
        } catch (err) {
            if (!saveCompletedRef.current) {
                setErrorMessage(err?.message || ERROR_MESSAGE);
                showErrorWithMinLoadingTime();
            }
        }
    }, [isLoading, saveBulkRules, showSuccessWithMinLoadingTime, showErrorWithMinLoadingTime, touchedConversions]);

    return {
        isLoading,
        showErrorModal,
        showSuccessModal,
        errorMessage,
        handleSaveAndClose,
        hideErrorModal: () => setShowErrorModal(false),
        hideSuccessModal: () => setShowSuccessModal(false),
    };
};
